import { Box, CircularProgress } from "@mui/material";
import { DateTime } from "luxon";
import { BookingType, IProviderData, IGroupData } from "types/appointmentBooking";
import { IDay, ISlot } from "./AvailabilityShared.helpers";

import { AvailabilityMonthModel, IdNameItemModel, ProviderModel } from "types/generated/models";
import CalendarHeader from "./CalendarHeader";
import CalendarContent from "./CalendarContent";

import styles from "./AvailabilityShared.module.css";

interface IAvailabilitySharedProps {
    currentMonth: DateTime;
    setCurrentMonth: (month: DateTime) => void;
    selectedDate: DateTime | null;
    setSelectedDate: (date: DateTime | null) => void;
    selectedSlot: ISlot | null;
    setSelectedSlot: (slot: ISlot | null) => void;
    availabilityData: AvailabilityMonthModel;
    weeks: Array<Array<IDay>>;
    isLoading: boolean;
    handleSlotSelection: (slot: ISlot, selectedProvider?: IdNameItemModel) => void;
    provider: IProviderData;
    bookingType: BookingType;
    availableProviders?: ProviderModel[];
    defaultStripeAccount?: string;
    onProviderSelect?: (providerData: IProviderData) => void;
    onGroupSelect?: (groupData: IGroupData) => void;
}

export default function AvailabilityShared({
    currentMonth,
    setCurrentMonth,
    selectedSlot,
    setSelectedSlot,
    availabilityData,
    isLoading,
    selectedDate,
    setSelectedDate,
    weeks,
    handleSlotSelection,
    provider,
    bookingType,
    availableProviders = [],
    defaultStripeAccount = "",
    onProviderSelect = () => {},
    onGroupSelect = () => {},
}: Readonly<IAvailabilitySharedProps>) {
    const goToPreviousMonth = () => {
        const newMonth = currentMonth.minus({ months: 1 });
        setCurrentMonth(newMonth);
        setSelectedDate(null);
        setSelectedSlot(null);
    };

    const goToNextMonth = () => {
        const newMonth = currentMonth.plus({ months: 1 });
        setCurrentMonth(newMonth);
        setSelectedDate(null);
        setSelectedSlot(null);
    };

    const handleDateClick = (date: DateTime) => {
        const isSameDate = selectedDate?.equals(date);
        setSelectedDate(isSameDate ? null : date);
        setSelectedSlot(null);
    };

    const slotOnChange = (slot: ISlot, selectedProvider?: IdNameItemModel) => {
        setSelectedSlot(slot);
        handleSlotSelection(slot, selectedProvider);
    };

    return (
        <Box className={styles.container} data-test-id="section_availability">
            <CalendarHeader
                currentMonth={currentMonth}
                goToPreviousMonth={goToPreviousMonth}
                goToNextMonth={goToNextMonth}
            />

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4} data-test-id="loading_indicator_availability">
                    <CircularProgress data-test-id="loading_spinner" />
                </Box>
            ) : (
                <CalendarContent
                    weeks={weeks}
                    availabilityData={availabilityData}
                    selectedDate={selectedDate}
                    handleDateClick={handleDateClick}
                    slotOnChange={slotOnChange}
                    selectedSlot={selectedSlot}
                    provider={provider}
                    availableProviders={availableProviders}
                    bookingType={bookingType}
                    defaultStripeAccount={defaultStripeAccount}
                    onProviderSelect={onProviderSelect}
                    onGroupSelect={onGroupSelect}
                />
            )}
        </Box>
    );
}
